import type React from 'react'
import { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useGetPublicAffiliateStatsQuery } from '../../redux/services/coreApi'
import { AffiliateStatsView } from '../App/screens/Admin/AffiliatePartners/components/AffiliatePartnerStats'

const PublicAffiliateStatsScreen: React.FC = () => {
  const location = useLocation()
  const history = useHistory()
  
  // Get URL search params
  const searchParams = new URLSearchParams(location.search)
  const urlApiKey = searchParams.get('apiKey') || ''
  const urlFromDate = searchParams.get('fromDate') || ''
  const urlToDate = searchParams.get('toDate') || ''
  
  const [apiKey, setApiKey] = useState(urlApiKey)
  const [fromDate, setFromDate] = useState(urlFromDate)
  const [toDate, setToDate] = useState(urlToDate)
  const [showForm, setShowForm] = useState(!urlApiKey)

  const {
    data: affiliateStats,
    isLoading,
    error,
  } = useGetPublicAffiliateStatsQuery(
    {
      apiKey,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    },
    {
      skip: !apiKey,
    }
  )

  // Update URL when form values change
  const updateUrl = (newApiKey: string, newFromDate: string, newToDate: string) => {
    const params = new URLSearchParams()
    if (newApiKey) params.set('apiKey', newApiKey)
    if (newFromDate) params.set('fromDate', newFromDate)
    if (newToDate) params.set('toDate', newToDate)
    
    const newSearch = params.toString()
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname
    history.replace(newUrl)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (apiKey.trim()) {
      updateUrl(apiKey, fromDate, toDate)
      setShowForm(false)
    }
  }

  const handleShowForm = () => {
    setShowForm(true)
  }

  const handleDateChange = (newFromDate: string, newToDate: string) => {
    setFromDate(newFromDate)
    setToDate(newToDate)
    if (apiKey) {
      updateUrl(apiKey, newFromDate, newToDate)
    }
  }

  return (
    <div className="min-h-screen bg-brand-light-background dark:bg-brand-dark-background">
      <div className="container mx-auto px-4 py-8">
        {showForm ? (
          <div className="max-w-md mx-auto">
            <div className="card p-6">
              <h1 className="text-2xl font-bold mb-6 text-center">
                Affiliate Partner Statistics
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
                Enter your API key to view your affiliate statistics
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label 
                    htmlFor="apiKey" 
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    API Key *
                  </label>
                  <input
                    type="password"
                    id="apiKey"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter your affiliate API key"
                    required
                  />
                </div>

                <div>
                  <label 
                    htmlFor="fromDate" 
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    From Date (Optional)
                  </label>
                  <input
                    type="date"
                    id="fromDate"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label 
                    htmlFor="toDate" 
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    To Date (Optional)
                  </label>
                  <input
                    type="date"
                    id="toDate"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  View Statistics
                </button>
              </form>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold mb-2">Your Affiliate Statistics</h1>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Bookmark this URL:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs max-w-md truncate">
                      {window.location.href}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(window.location.href)}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 text-xs underline"
                      title="Copy URL to clipboard"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Date filters */}
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => handleDateChange(e.target.value, toDate)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => handleDateChange(fromDate, e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                    placeholder="To"
                  />
                </div>
                
                <button
                  onClick={handleShowForm}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm whitespace-nowrap"
                >
                  Change API Key
                </button>
              </div>
            </div>

            {error && (
              <div className="card p-4 mb-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                <p className="text-red-600 dark:text-red-400">
                  Failed to load statistics. Please check your API key and try again.
                </p>
              </div>
            )}

            <AffiliateStatsView
              affiliateStats={affiliateStats}
              isLoading={isLoading}
              showTitle={false}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default PublicAffiliateStatsScreen
